import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { omit } from "lodash-es";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import RoundButton from "@app/component/RoundButton";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";

import ResourceContentType, { getResourceContentTypeName } from "../../enum/ResourceContentType";
import { getResourcesViewState } from "../../store";
import { fetchResources } from "../../store/actions";
import getResourceContentTypeFilter, {
  getResourceContentTypeFromFilter,
} from "../../utils/getResourceContentTypeFilter";

import messages from "./messages";
import styles from "./ToolBar.module.scss";

const CLEAR_OPTION_VALUE = "clear";

const ResourceTypeFilter: FC<typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>> = ({
  onFetchResources,
  viewState,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const { filter, range } = viewState;

  const firstPageRange = useMemo(
    () => ({ endRow: range.endRow - range.startRow, startRow: 0 }),
    [range.endRow, range.startRow]
  );

  const resourceTypeItems: MenuProps["items"] = useMemo(
    () => [
      {
        className: styles.clearMenuItem,
        key: CLEAR_OPTION_VALUE,
        label: intl.formatMessage(messages.clear),
      },
      ...Object.values(ResourceContentType).map((type) => ({
        key: type,
        label: getResourceContentTypeName(intl, type),
      })),
    ],
    [intl]
  );

  const selectedResourceType = useMemo(
    () => (viewState.filter ? getResourceContentTypeFromFilter(viewState.filter) : undefined),
    [viewState.filter]
  );

  const handleMenuClick = useCallback(
    (item: MenuInfo) => {
      const key = item.key as ResourceContentType | typeof CLEAR_OPTION_VALUE;
      onFetchResources({
        filter:
          key === CLEAR_OPTION_VALUE
            ? omit(filter, ResourceFieldName.TYPE, ResourceFieldName.URL)
            : { ...filter, ...getResourceContentTypeFilter(key) },
        range: firstPageRange,
      });
      analytics.clicked([
        "Resource Type",
        key === "clear" ? "Clear" : getResourceContentTypeName(intl, key),
      ]);
    },
    [onFetchResources, filter, firstPageRange, analytics, intl]
  );
  return (
    <Dropdown
      menu={{
        items: resourceTypeItems,
        onClick: handleMenuClick,
        selectedKeys: selectedResourceType ? [selectedResourceType] : [],
      }}
      trigger={["click"]}
    >
      <RoundButton
        active={!!selectedResourceType}
        rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
      >
        {selectedResourceType
          ? getResourceContentTypeName(intl, selectedResourceType)
          : intl.formatMessage(messages.resourceType)}
      </RoundButton>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  viewState: getResourcesViewState(state),
});

const mapDispatchToProps = {
  onFetchResources: fetchResources.request,
};
export default connect(mapStateToProps, mapDispatchToProps)(ResourceTypeFilter);
